/**
 * Shared notes UI for summarization detail panels.
 *
 * Purpose:
 * - `NotesSection` — the standard "Notes" block used across detail panels:
 *   an uppercase header with a pencil edit toggle, a collapsible markdown
 *   preview when read-only, and an inline textarea + Save/Cancel when editing.
 * - `CollapsibleMarkdown` — renders bullet-normalised markdown with a
 *   Show more / Show less toggle once the content exceeds the collapse height.
 *
 * Used by: SecurityInterestDetailPanel, EmployeeDetailPanel (and any other
 * detail panel that needs a consistent notes/description section).
 */

import { useLayoutEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Pencil } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { Textarea } from "@/ui/primitives/textarea";
import { cn } from "@/utils/misc";
import { normaliseForMarkdown } from "./NotesSection.helpers";

const COLLAPSED_MAX_HEIGHT_PX = 180;

/**
 * Markdown block that collapses to a fixed height and exposes a
 * Show more / Show less toggle when the content overflows.
 */
export function CollapsibleMarkdown({
  text,
  emptyLabel = "None",
}: {
  text: string;
  emptyLabel?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const normalised = text ? normaliseForMarkdown(text) : "";

  // Measure the natural content height against the collapse threshold.
  // `scrollHeight` reports the full content height regardless of the
  // `max-height` clamp, so this stays accurate whether the block is
  // expanded or collapsed (otherwise the toggle disappears once expanded).
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > COLLAPSED_MAX_HEIGHT_PX + 1);
  }, [normalised]);

  if (!normalised)
    return <p className="text-sm italic text-muted-foreground">{emptyLabel}</p>;

  return (
    <div>
      <div
        ref={contentRef}
        className={cn(
          "prose prose-sm relative max-w-none overflow-hidden transition-[max-height] duration-200 dark:prose-invert [&_li]:text-sm [&_li]:leading-relaxed [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-foreground [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-4",
          !expanded && "max-h-[180px]",
        )}
      >
        <ReactMarkdown>{normalised}</ReactMarkdown>
        {!expanded && overflows && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
        )}
      </div>
      {overflows && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 rounded-md border border-primary/30 px-3 py-1 text-xs font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/5"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

interface NotesSectionProps {
  /** Current notes value (markdown-ish plain text). */
  value: string;
  /** Persist the edited notes value. */
  onSave: (value: string) => void;
  /** When true, the edit affordance is hidden and the block is read-only. */
  isViewer?: boolean;
  /** Section heading (defaults to "Notes"). */
  label?: string;
  /** Placeholder shown when there are no notes. */
  emptyLabel?: string;
}

/**
 * Standard notes block: uppercase header + pencil edit toggle, collapsible
 * markdown preview when read-only, inline textarea with Save/Cancel when
 * editing.
 */
export function NotesSection({
  value,
  onSave,
  isViewer = false,
  label = "Notes",
  emptyLabel = "No notes yet",
}: NotesSectionProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  return (
    <div className="space-y-3 py-2">
      <div className="flex items-center justify-between">
        <h4 className="!font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
          {label}
        </h4>
        {!isViewer && !editing && (
          <button
            onClick={() => {
              setEditing(true);
              setDraft(value);
            }}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      <div>
        {editing ? (
          <div className="space-y-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setEditing(false);
              }}
              rows={5}
              className="text-sm"
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  onSave(draft);
                  setEditing(false);
                }}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <CollapsibleMarkdown text={value} emptyLabel={emptyLabel} />
        )}
      </div>
    </div>
  );
}
